import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { format, parseISO, addDays } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ScanLine, Camera, CameraOff, Trash2, Send, ArrowLeft, ListChecks,
  PackageCheck, ClipboardList, X, Keyboard, ChevronLeft, ChevronRight, Download, Printer, Pencil, PackagePlus, Store, Monitor, Tv,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DENOMS = [1, 2, 3, 5, 10, 20, 30, 50];
const MAX_SLOTS = 30;
const DEFAULT_PACK_SIZES = { 1: 300, 2: 150, 3: 100, 5: 100, 10: 60, 20: 30, 30: 30, 50: 30 };
const pad3 = (n) => String(Math.max(0, n)).padStart(3, "0");
// $ value of a received pack entry: denom × number of tickets × packs
const packValue = (s) => {
  const st = parseInt(s.ticket_start, 10);
  const en = parseInt(s.ticket_end, 10);
  if (s.mode !== "received" || !s.denom || isNaN(st) || isNaN(en) || en < st) return null;
  return s.denom * (en - st + 1) * (s.packs || 1);
};

const MODES = [
  { key: "log", label: "Scan Log", icon: ListChecks, hint: "Just record each scanned ticket with date & time." },
  { key: "received", label: "Pack Received", icon: PackageCheck, hint: "Scan a pack → saved as a delivery with its game name and auto ticket numbers (000 to pack size − 1 from Settings), and adds +1 to that day's Delivery count. A repeat scan of the same game + denomination merges as an extra pack — no duplicate entries." },
  { key: "to_counter", label: "Pack to Counter", icon: Store, hint: "Scan a pack as it goes out to the sales counter — lowers that day's Ending packs by 1 (auto-subtracts from main inventory; seeds Ending from Opening + Delivery − LVM when blank). Needs a denomination: pick a pill or scan a remembered game." },
  { key: "to_lvm1", label: "Pack to LVM-1", icon: Monitor, hint: "Scan a pack loaded into vending machine 1 — adds +1 to the LVM-1 column on that day's Daily Entry. Needs a denomination: pick a pill or scan a remembered game." },
  { key: "to_lvm2", label: "Pack to LVM-2", icon: Tv, hint: "Scan a pack loaded into vending machine 2 — adds +1 to the LVM-2 column on that day's Daily Entry. Needs a denomination: pick a pill or scan a remembered game." },
  { key: "closing", label: "Closing Numbers", icon: ClipboardList, hint: "Pick a denomination — or just scan: remembered games route to the right denomination automatically. Scan tickets top-to-bottom in display order, then send them to today's Daily Entry." },
];

const LEGACY_MODE_LABELS = { activated: "Pack Activated" };

const MODE_BADGE = {
  log: "bg-sky-500/20 text-sky-300",
  received: "bg-emerald-500/20 text-emerald-300",
  activated: "bg-amber-500/20 text-amber-300",
  to_counter: "bg-cyan-500/20 text-cyan-300",
  to_lvm1: "bg-amber-500/20 text-amber-300",
  to_lvm2: "bg-orange-500/20 text-orange-300",
  closing: "bg-purple-500/20 text-purple-300",
};

// Barcode rule (user spec, KY ticket e.g. 758-528753-4-041): GAME = first 3 digits,
// PACK = next 6 digits, TICKET = last 3 digits; everything in between ignored.
// Scanner quirks handled: ITF leading-zero padding (14 digits) is stripped;
// the front price UPC (7 13156 xxxxx x) is rejected with a hint.
const isPriceUpc = (digits) => digits.length === 12 && digits.startsWith("713156");
const parseScan = (raw) => {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 14 && digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length >= 12) return { game: digits.slice(0, 3), pack: digits.slice(3, 9), ticket: digits.slice(-3) };
  if (digits.length >= 9) return { game: digits.slice(0, 3), pack: digits.slice(3, 9), ticket: digits.slice(9) };
  if (digits.length >= 3) return { game: "", pack: "", ticket: digits.slice(-3) };
  return { game: "", pack: "", ticket: "" };
};

const beep = (freq = 880, dur = 0.09) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "square";
    o.frequency.value = freq;
    g.gain.value = 0.08;
    o.start();
    o.stop(ctx.currentTime + dur);
    setTimeout(() => ctx.close().catch(() => {}), 400);
  } catch { /* no audio available */ }
};

export default function LottoScan() {
  const navigate = useNavigate();
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [mode, setMode] = useState("log");
  const [denom, setDenom] = useState(null);
  const [scans, setScans] = useState([]);
  const [viewDate, setViewDate] = useState(todayStr);
  const [gameMap, setGameMap] = useState({});
  const [packSizes, setPackSizes] = useState(DEFAULT_PACK_SIZES);
  const [deliveryDialog, setDeliveryDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [closingQueue, setClosingQueue] = useState({});
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef(null);
  const qrRef = useRef(null);
  const lastCamScan = useRef({ text: "", ts: 0 });
  const modeRef = useRef(mode);
  const denomRef = useRef(denom);
  const viewDateRef = useRef(viewDate);
  const gameMapRef = useRef(gameMap);
  const packSizesRef = useRef(packSizes);
  modeRef.current = mode;
  denomRef.current = denom;
  viewDateRef.current = viewDate;
  gameMapRef.current = gameMap;
  packSizesRef.current = packSizes;

  useEffect(() => {
    axios.get(`${API}/lotto-scans?date=${viewDate}`)
      .then((res) => setScans(res.data.scans || []))
      .catch(() => {});
  }, [viewDate]);

  useEffect(() => {
    axios.get(`${API}/lotto-scans/game-map`)
      .then((res) => {
        const m = res.data.map || {};
        const norm = {};
        Object.entries(m).forEach(([g, v]) => {
          norm[g] = v && typeof v === "object" ? v : { denom: Number(v), name: "" };
        });
        setGameMap(norm);
      })
      .catch(() => {});
    axios.get(`${API}/user/settings`)
      .then((res) => {
        if (res.data?.pack_sizes) {
          const ps = { ...DEFAULT_PACK_SIZES };
          Object.keys(ps).forEach((d) => {
            const v = parseInt(res.data.pack_sizes[d]);
            if (v > 0) ps[d] = v;
          });
          setPackSizes(ps);
        }
      })
      .catch(() => {});
  }, []);

  const [profile, setProfile] = useState(null);
  useEffect(() => {
    axios.get(`${API}/user/profile`).then((res) => setProfile(res.data)).catch(() => {});
  }, []);

  useEffect(() => () => {
    if (qrRef.current) {
      qrRef.current.stop().then(() => qrRef.current?.clear()).catch(() => {});
    }
  }, []);

  const upsertScanRow = useCallback((scan) => {
    if (viewDateRef.current !== todayStr) return;
    setScans((prev) => {
      const idx = prev.findIndex((s) => s.id === scan.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = scan;
        return next;
      }
      return [scan, ...prev];
    });
  }, [todayStr]);

  const saveDelivery = useCallback(async ({ raw, game, pack, ticket, denom: d, name }) => {
    const ps = packSizesRef.current[d] || DEFAULT_PACK_SIZES[d] || 30;
    try {
      const res = await axios.post(`${API}/lotto-scans`, {
        raw, mode: "received", game, pack, ticket, denom: d, date: todayStr,
        game_name: name, ticket_start: "000", ticket_end: pad3(ps - 1),
      });
      if (res.data.duplicate_blocked) {
        beep(330);
        toast.warning("This pack was already recorded today");
        return true;
      }
      upsertScanRow(res.data.scan);
      beep(880);
      if (res.data.merged) {
        toast.success(`${name} ($${d}): +1 pack — now ${res.data.scan.packs} packs on this delivery`);
      } else {
        toast.success(`Delivery saved: ${name} ($${d}) · tickets 000–${pad3(ps - 1)}`);
      }
      if (res.data.delivery_updated) {
        const du = res.data.delivery_updated;
        toast.success(`Delivery +1 → $${du.denom} now ${du.delivery || 0} on today's entry`);
      }
      if (game) setGameMap((prev) => ({ ...prev, [game]: { denom: d, name } }));
      return true;
    } catch (err) {
      beep(220);
      toast.error(err.response?.data?.detail || "Failed to save delivery");
      return false;
    }
  }, [todayStr, upsertScanRow]);

  const commitScan = useCallback(async (raw) => {
    const clean = (raw || "").trim();
    if (!clean) return;
    if (isPriceUpc(clean.replace(/\D/g, ""))) {
      beep(220);
      toast.error("That looks like the price barcode on the front — scan the long GAME-PACK-TICKET barcode near the bottom of the ticket");
      return;
    }
    const m = modeRef.current;
    const parsed = parseScan(clean);
    // Remembered game → denomination auto-routing (no pill click needed)
    let d = denomRef.current;
    let autoRouted = false;
    const known = parsed.game ? gameMapRef.current[parsed.game] : null;
    if (!d && known?.denom) {
      d = Number(known.denom);
      autoRouted = true;
    }
    if (m === "received") {
      const name = (known?.name || "").trim();
      if (!d || !name) {
        beep(660);
        setDeliveryDialog({ raw: clean, game: parsed.game, pack: parsed.pack, ticket: parsed.ticket, denom: d || null, name });
        return;
      }
      await saveDelivery({ raw: clean, game: parsed.game, pack: parsed.pack, ticket: parsed.ticket, denom: d, name });
      return;
    }
    if ((m === "closing" || m === "to_counter" || m === "to_lvm1" || m === "to_lvm2") && !d) {
      beep(220);
      toast.error("Pick a denomination ($1–$50) — or scan a game that's been scanned before");
      return;
    }
    try {
      const res = await axios.post(`${API}/lotto-scans`, {
        raw: clean, mode: m, ...parsed, denom: d || null, date: todayStr,
      });
      const scan = res.data.scan;
      if (viewDateRef.current === todayStr) setScans((prev) => [scan, ...prev]);
      if (scan.duplicate) {
        beep(330);
        toast.warning("Duplicate — this code was already scanned today");
      } else if (autoRouted) {
        beep(880);
        toast.success(`Game ${parsed.game} → $${d} (remembered)`);
      } else {
        beep(880);
      }
      if (res.data.delivery_updated) {
        const du = res.data.delivery_updated;
        toast.success(`Delivery +1 → $${du.denom} now ${du.delivery || 0} on today's entry`);
      }
      if (res.data.inv_updated) {
        const iu = res.data.inv_updated;
        const fieldNames = { lvm: "LVM-1", lvm2: "LVM-2", endingInventory: "Ending packs" };
        toast.success(`${fieldNames[iu.field] || iu.field} → $${iu.denom} now ${iu.value || 0} on today's entry`);
        if (iu.clamped) toast.warning(`$${iu.denom} Ending was already 0 — double-check your pack counts`);
      }
      // Learn locally: explicit denomination choice in closing mode teaches the map
      if (m === "closing" && !autoRouted && parsed.game && d) {
        setGameMap((prev) => (prev[parsed.game]?.denom === d ? prev : { ...prev, [parsed.game]: { denom: d, name: prev[parsed.game]?.name || "" } }));
      }
      if (m === "closing" && parsed.ticket) {
        setClosingQueue((prev) => {
          const arr = prev[d] || [];
          if (arr.length >= MAX_SLOTS) {
            toast.error(`$${d} already has ${MAX_SLOTS} scanned rows (max)`);
            return prev;
          }
          return { ...prev, [d]: [...arr, parsed.ticket] };
        });
      }
    } catch (err) {
      beep(220);
      toast.error(err.response?.data?.detail || "Failed to save scan");
    }
  }, [todayStr, saveDelivery]);

  const forgetGame = async (game) => {
    try {
      await axios.delete(`${API}/lotto-scans/game-map/${game}`);
      setGameMap((prev) => {
        const next = { ...prev };
        delete next[game];
        return next;
      });
    } catch {
      toast.error("Failed to forget game");
    }
  };

  const saveEdit = async () => {
    const e0 = editDialog;
    if (!e0) return;
    try {
      const res = await axios.put(`${API}/lotto-scans/${e0.id}`, {
        game_name: e0.game_name || "",
        denom: e0.denom,
        packs: parseInt(e0.packs) || 1,
        ticket_start: e0.ticket_start || "",
        ticket_end: e0.ticket_end || "",
      });
      upsertScanRow(res.data.scan);
      if (viewDate !== todayStr) {
        setScans((prev) => prev.map((s) => (s.id === res.data.scan.id ? res.data.scan : s)));
      }
      if (res.data.delivery_updated) {
        const du = res.data.delivery_updated;
        toast.info(`Delivery adjusted → $${du.denom} now ${du.delivery || 0} on that day's entry`);
      }
      const sc = res.data.scan;
      if (sc.game) setGameMap((prev) => ({ ...prev, [sc.game]: { denom: sc.denom, name: sc.game_name || "" } }));
      toast.success("Delivery updated");
      setEditDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update delivery");
    }
  };

  const MOVE_MODES = ["activated", "to_counter", "to_lvm1", "to_lvm2"];
  const packScans = scans.filter((s) => s.mode === "received" || MOVE_MODES.includes(s.mode));

  const printSlip = () => {
    if (packScans.length === 0) return;
    const chrono = [...packScans].reverse();
    const fmtTime = (iso) => { try { return format(parseISO(iso), "h:mm a"); } catch { return ""; } };
    const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const nonDup = (list) => list.filter((s) => !s.duplicate);
    const section = (label, list) => {
      if (list.length === 0) return "";
      const rows = list.map((s) => `<tr>
        <td class="t">${esc(fmtTime(s.created_at))}</td>
        <td class="d">${s.denom ? `$${esc(s.denom)}` : "—"}</td>
        <td class="g">${s.game_name ? `${esc(s.game_name)} ` : ""}${esc(s.game || "")}${s.game && s.pack ? "-" : ""}${esc(s.pack || "")}</td>
        <td class="k">${s.mode === "received" ? `${esc(s.ticket_start || "000")}–${esc(s.ticket_end || "")}${(s.packs || 1) > 1 ? ` ×${s.packs}` : ""}` : (s.ticket ? `#${esc(s.ticket)}` : "")}${s.duplicate ? " DUP" : ""}</td>
        <td class="v">${packValue(s) !== null ? `$${packValue(s).toLocaleString()}` : ""}</td>
      </tr>`).join("");
      return `<div class="sec"><div class="sech">${label} (${nonDup(list).reduce((t, s) => t + (s.packs || 1), 0)})</div><table>${rows}</table></div>`;
    };
    const counts = (list) => {
      const by = {};
      nonDup(list).forEach((s) => { const k = s.denom ? `$${s.denom}` : "—"; by[k] = (by[k] || 0) + (s.packs || 1); });
      return Object.entries(by).map(([k, n]) => `${k}×${n}`).join("  ") || "none";
    };
    const received = chrono.filter((s) => s.mode === "received");
    const toCounter = chrono.filter((s) => s.mode === "to_counter");
    const toLvm1 = chrono.filter((s) => s.mode === "to_lvm1");
    const toLvm2 = chrono.filter((s) => s.mode === "to_lvm2");
    const activated = chrono.filter((s) => s.mode === "activated");
    const company = profile?.company_name || profile?.store_name || "";
    const dateLabel = format(parseISO(viewDate), "EEE, MMM d, yyyy");
    const html = `<!doctype html><html><head><title>Pack Slip ${viewDate}</title><style>
      @page { margin: 10mm; }
      body { margin: 0; font-family: 'Courier New', monospace; color: #000; }
      .slip { width: 300px; margin: 0 auto; font-size: 11px; }
      .co { text-align: center; font-weight: 800; font-size: 14px; }
      .hd { text-align: center; font-size: 12px; font-weight: bold; margin: 2px 0 4px; }
      .rule { border-top: 1px dashed #000; margin: 5px 0; }
      .sech { font-weight: bold; font-size: 11px; margin: 4px 0 2px; }
      table { width: 100%; border-collapse: collapse; }
      td { padding: 1px 2px; font-size: 10.5px; white-space: nowrap; }
      .t { width: 58px; } .d { width: 32px; font-weight: bold; } .k { text-align: right; } .v { text-align: right; font-weight: bold; width: 48px; }
      .sum { font-size: 10.5px; margin: 2px 0; }
      .tot { font-weight: 800; font-size: 12px; text-align: center; margin: 4px 0; }
      .ft { text-align: center; font-size: 9px; color: #333; margin-top: 6px; }
    </style></head><body><div class="slip">
      ${company ? `<div class="co">${esc(company)}</div>` : ""}
      <div class="hd">PACK SLIP — ${esc(dateLabel)}</div>
      <div class="rule"></div>
      ${section("PACKS RECEIVED", received)}
      ${section("PACKS TO COUNTER", toCounter)}
      ${section("PACKS TO LVM-1", toLvm1)}
      ${section("PACKS TO LVM-2", toLvm2)}
      ${section("PACKS ACTIVATED", activated)}
      <div class="rule"></div>
      <div class="sum">Received: ${esc(counts(received))}</div>
      ${toCounter.length ? `<div class="sum">To Counter: ${esc(counts(toCounter))}</div>` : ""}
      ${toLvm1.length ? `<div class="sum">To LVM-1: ${esc(counts(toLvm1))}</div>` : ""}
      ${toLvm2.length ? `<div class="sum">To LVM-2: ${esc(counts(toLvm2))}</div>` : ""}
      ${activated.length ? `<div class="sum">Activated: ${esc(counts(activated))}</div>` : ""}
      <div class="tot">RECEIVED: ${nonDup(received).reduce((t, s) => t + (s.packs || 1), 0)} PACKS — $${nonDup(received).reduce((t, s) => t + (packValue(s) || 0), 0).toLocaleString()}</div>
      <div class="tot">MOVED OUT: ${nonDup([...toCounter, ...toLvm1, ...toLvm2, ...activated]).length} PACKS</div>
      <div class="rule"></div>
      <div class="ft">Printed ${esc(format(new Date(), "MMM d, yyyy h:mm a"))} — Lotto Scanning</div>
    </div></body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => iframe.remove(), 3000);
    }, 150);
  };

  const shiftViewDate = (delta) => {
    const next = format(addDays(parseISO(viewDate), delta), "yyyy-MM-dd");
    if (next > todayStr) return;
    setViewDate(next);
  };

  const handleInputKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitScan(inputVal);
      setInputVal("");
    }
  };

  const startCamera = async () => {
    setCameraBusy(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const qr = new Html5Qrcode("lotto-cam-view");
      qrRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 300, height: 140 } },
        (text) => {
          const now = Date.now();
          if (text === lastCamScan.current.text && now - lastCamScan.current.ts < 2500) return;
          lastCamScan.current = { text, ts: now };
          commitScan(text);
        },
        () => {},
      );
      setCameraOn(true);
    } catch {
      toast.error("Could not start camera — check camera permission, or use a scanner gun / type the code");
      qrRef.current = null;
    } finally {
      setCameraBusy(false);
    }
  };

  const stopCamera = async () => {
    setCameraBusy(true);
    try {
      if (qrRef.current) {
        await qrRef.current.stop();
        qrRef.current.clear();
        qrRef.current = null;
      }
    } catch { /* already stopped */ }
    setCameraOn(false);
    setCameraBusy(false);
    inputRef.current?.focus();
  };

  const deleteScan = async (id) => {
    const removed = scans.find((s) => s.id === id);
    try {
      const res = await axios.delete(`${API}/lotto-scans/${id}`);
      setScans((prev) => prev.filter((s) => s.id !== id));
      if (res.data.delivery_updated) {
        const du = res.data.delivery_updated;
        toast.info(`Delivery −${removed?.packs || 1} → $${du.denom} now ${du.delivery || 0} on that day's entry`);
      }
      if (res.data.inv_updated) {
        const iu = res.data.inv_updated;
        const fieldNames = { lvm: "LVM-1", lvm2: "LVM-2", endingInventory: "Ending packs" };
        toast.info(`${fieldNames[iu.field] || iu.field} reversed → $${iu.denom} now ${iu.value || 0} on that day's entry`);
      }
    } catch {
      toast.error("Failed to delete scan");
    }
  };

  const exportScans = async () => {
    try {
      const res = await axios.get(`${API}/lotto-scans-export?date=${viewDate}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lotto_scans_${viewDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Scan history exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const removeFromQueue = (d, idx) => {
    setClosingQueue((prev) => ({ ...prev, [d]: prev[d].filter((_, i) => i !== idx) }));
  };

  const queueDenoms = DENOMS.filter((d) => (closingQueue[d] || []).length > 0);
  const queueCount = queueDenoms.reduce((s, d) => s + closingQueue[d].length, 0);

  const sendToEntry = () => {
    if (queueCount === 0) return;
    const numbers = {};
    queueDenoms.forEach((d) => { numbers[d] = closingQueue[d]; });
    localStorage.setItem("lotto_scan_closing", JSON.stringify({ date: todayStr, ts: Date.now(), numbers }));
    toast.success("Opening today's entry with your scanned closing numbers…");
    navigate(`/entry/${todayStr}`);
  };

  const activeMode = MODES.find((m) => m.key === mode);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="lotto-scan-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="lotto-scan-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-mono text-3xl font-bold tracking-tight flex items-center gap-3">
              <ScanLine className="w-7 h-7 text-primary" /> Lotto Scanning
            </h1>
            <p className="text-emerald-950/70 mt-1 text-sm font-mono font-medium">{format(new Date(), "EEEE, MMMM d, yyyy")} — every scan is logged with date &amp; time</p>
          </div>
        </div>
      </div>

      {/* Mode selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="lotto-scan-modes">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => { setMode(m.key); inputRef.current?.focus(); }}
              className={`rounded-xl border px-3 py-3 font-mono text-sm font-bold flex items-center gap-2 transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/50"}`}
              data-testid={`lotto-mode-${m.key}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {m.label}
            </button>
          );
        })}
      </div>
      <p className="font-mono text-xs font-medium text-emerald-950/80 -mt-3" data-testid="lotto-mode-hint">{activeMode.hint}</p>

      {/* Denomination picker (required for closing, optional tag otherwise) */}
      <div className="flex flex-wrap items-center gap-2" data-testid="lotto-denom-picker">
        <span className="font-mono text-xs font-medium text-emerald-950/80 mr-1">
          Denomination{["closing", "to_counter", "to_lvm1", "to_lvm2"].includes(mode) ? <span className="text-rose-700 font-bold"> (required unless game is remembered)</span> : " (optional tag)"}:
        </span>
        {DENOMS.map((d) => (
          <button
            key={d}
            onClick={() => { setDenom(denom === d ? null : d); inputRef.current?.focus(); }}
            className={`px-3 py-1.5 rounded-lg font-mono text-sm font-bold border transition-colors ${denom === d ? "bg-emerald-500 text-black border-emerald-400" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
            data-testid={`lotto-denom-${d}`}
          >
            ${d}
          </button>
        ))}
      </div>

      {/* Remembered game → denomination mappings */}
      {Object.keys(gameMap).length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="lotto-game-map">
          <span className="font-mono text-xs font-medium text-emerald-950/80 mr-1">Remembered games (auto-route):</span>
          {Object.entries(gameMap).map(([g, v]) => (
            <span key={g} className="inline-flex items-center gap-1.5 bg-card border border-border rounded-lg px-2 py-1 font-mono text-xs" data-testid={`lotto-game-chip-${g}`}>
              {g} → <span className="text-emerald-300 font-bold">${v.denom}</span>
              {v.name && <span className="text-muted-foreground">· {v.name}</span>}
              <button onClick={() => forgetGame(g)} className="text-muted-foreground hover:text-rose-400" title="Forget this game" data-testid={`lotto-game-forget-${g}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scan input card */}
        <Card className="border-border/50" data-testid="lotto-scan-input-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-mono text-lg flex items-center gap-2">
              <Keyboard className="w-5 h-5 text-primary" /> Scanner Gun / Manual Entry
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="font-mono text-xs text-muted-foreground">
              Click the box below, then scan with a USB/Bluetooth scanner gun — or type the barcode digits and press Enter. Reads the first 9 digits (Game 3 + Pack 6) and last 3 (ticket #), e.g. 758-528753-4-041.
            </p>
            <Input
              ref={inputRef}
              autoFocus
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={handleInputKey}
              placeholder="Scan or type barcode, then Enter…"
              className="font-mono text-lg h-12 bg-yellow-100 text-black placeholder:text-stone-500"
              data-testid="lotto-scan-input"
            />
            <div className="flex items-center gap-3">
              <Button
                onClick={() => { commitScan(inputVal); setInputVal(""); inputRef.current?.focus(); }}
                className="btn-primary"
                disabled={!inputVal.trim()}
                data-testid="lotto-scan-submit-btn"
              >
                <ScanLine className="w-4 h-4 mr-1" /> Record Scan
              </Button>
              {mode === "received" && (
                <Button
                  variant="outline"
                  onClick={() => setDeliveryDialog({ raw: `MANUAL-${Date.now()}`, game: "", pack: "", ticket: "", denom: denom || null, name: "" })}
                  data-testid="lotto-add-delivery-btn"
                >
                  <PackagePlus className="w-4 h-4 mr-1" /> Add Delivery
                </Button>
              )}
              {!cameraOn ? (
                <Button variant="outline" onClick={startCamera} disabled={cameraBusy} data-testid="lotto-camera-start-btn">
                  <Camera className="w-4 h-4 mr-1" /> {cameraBusy ? "Starting…" : "Use Camera"}
                </Button>
              ) : (
                <Button variant="outline" onClick={stopCamera} disabled={cameraBusy} data-testid="lotto-camera-stop-btn">
                  <CameraOff className="w-4 h-4 mr-1" /> Stop Camera
                </Button>
              )}
            </div>
            <div id="lotto-cam-view" className={`rounded-lg overflow-hidden ${cameraOn ? "border border-primary/50" : ""}`} data-testid="lotto-camera-view" />
          </CardContent>
        </Card>

        {/* Closing-numbers queue */}
        {mode === "closing" ? (
          <Card className="border-purple-500/40" data-testid="lotto-closing-queue">
            <CardHeader className="pb-3">
              <CardTitle className="font-mono text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-purple-400" /> Closing Numbers Queue
                <span className="text-xs text-muted-foreground font-normal">({queueCount} scanned)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {queueCount === 0 ? (
                <p className="font-mono text-xs text-muted-foreground" data-testid="lotto-queue-empty">
                  Pick a denomination above, then scan each ticket top-to-bottom in display order. Row 1 = first scan, row 2 = second scan…
                </p>
              ) : (
                <div className="space-y-2">
                  {queueDenoms.map((d) => (
                    <div key={d} className="flex flex-wrap items-center gap-1.5" data-testid={`lotto-queue-denom-${d}`}>
                      <span className="font-mono text-xs font-bold text-emerald-300 w-8">${d}</span>
                      {closingQueue[d].map((t, i) => (
                        <span key={`${t}-${i}`} className="inline-flex items-center gap-1 bg-purple-500/20 text-purple-200 font-mono text-xs px-2 py-1 rounded-md" data-testid={`lotto-queue-chip-${d}-${i}`}>
                          <span className="text-purple-400">{i + 1}.</span>{t}
                          <button onClick={() => removeFromQueue(d, i)} className="hover:text-rose-400" data-testid={`lotto-queue-remove-${d}-${i}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={sendToEntry} disabled={queueCount === 0} className="btn-primary w-full" data-testid="lotto-send-to-entry-btn">
                <Send className="w-4 h-4 mr-2" /> Send to Today's Daily Entry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50" data-testid="lotto-scan-stats">
            <CardHeader className="pb-3">
              <CardTitle className="font-mono text-lg">{viewDate === todayStr ? "Today at a Glance" : `${format(parseISO(viewDate), "MMM d")} at a Glance`}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 mb-3" data-testid="lotto-stat-delivery-value">
                <p className="font-mono text-[11px] text-emerald-300/90 font-medium">Delivery Value {viewDate === todayStr ? "Today" : format(parseISO(viewDate), "MMM d")}</p>
                <p className="font-mono text-3xl font-bold text-emerald-400">
                  ${scans.reduce((t, s) => t + (packValue(s) || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {MODES.map((m) => {
                  const count = scans.filter((s) => s.mode === m.key).reduce((t, s) => t + (m.key === "received" ? (s.packs || 1) : 1), 0);
                  return (
                    <div key={m.key} className="rounded-lg border border-border/60 p-3" data-testid={`lotto-stat-${m.key}`}>
                      <p className="font-mono text-[11px] text-muted-foreground">{m.label}</p>
                      <p className="font-mono text-2xl font-bold text-primary">{count}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Scan log with date picker */}
      <Card className="border-border/50" data-testid="lotto-scan-log">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="font-mono text-lg" data-testid="lotto-log-title">
              {viewDate === todayStr ? "Today's Scans" : `Scans for ${format(parseISO(viewDate), "MMM d, yyyy")}`} ({scans.length})
            </CardTitle>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftViewDate(-1)} title="Previous day" data-testid="lotto-date-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <input
                type="date"
                value={viewDate}
                max={todayStr}
                onChange={(e) => { if (e.target.value && e.target.value <= todayStr) setViewDate(e.target.value); }}
                className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs"
                data-testid="lotto-date-input"
              />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shiftViewDate(1)} disabled={viewDate >= todayStr} title="Next day" data-testid="lotto-date-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              {viewDate !== todayStr && (
                <Button size="sm" className="h-8 bg-amber-500 text-black hover:bg-amber-400 font-mono font-bold" onClick={() => setViewDate(todayStr)} data-testid="lotto-date-today">
                  Today
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 font-mono" onClick={exportScans} disabled={scans.length === 0} title="Download this day's scans as Excel" data-testid="lotto-export-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Excel
              </Button>
              <Button size="sm" variant="outline" className="h-8 font-mono" onClick={printSlip} disabled={packScans.length === 0} title="Print an end-of-day slip of packs received & moved (counter / LVM)" data-testid="lotto-print-slip-btn">
                <Printer className="w-3.5 h-3.5 mr-1" /> Pack Slip
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {scans.length === 0 ? (
            <p className="font-mono text-sm text-muted-foreground py-6 text-center" data-testid="lotto-log-empty">
              {viewDate === todayStr ? "No scans yet today — scan a ticket above to get started." : "No scans recorded on this day."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-xs">
                <thead>
                  <tr className="table-header text-left">
                    <th className="p-2">Time</th>
                    <th className="p-2">Mode</th>
                    <th className="p-2">Game</th>
                    <th className="p-2">Name</th>
                    <th className="p-2">Pack</th>
                    <th className="p-2">Tickets</th>
                    <th className="p-2">Denom</th>
                    <th className="p-2">Packs</th>
                    <th className="p-2">Value</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((s) => (
                    <tr key={s.id} className={`border-b border-border/30 ${s.duplicate ? "bg-amber-500/10" : ""}`} data-testid={`lotto-scan-row-${s.id}`}>
                      <td className="p-2 text-muted-foreground whitespace-nowrap">{s.created_at ? format(parseISO(s.created_at), "h:mm:ss a") : ""}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${MODE_BADGE[s.mode] || "bg-muted"}`}>
                          {(MODES.find((m) => m.key === s.mode) || { label: LEGACY_MODE_LABELS[s.mode] || s.mode }).label}
                        </span>
                        {s.duplicate && <span className="ml-1 text-amber-400 text-[10px] font-bold">DUP</span>}
                      </td>
                      <td className="p-2">{s.game || "—"}</td>
                      <td className="p-2">{s.game_name || "—"}</td>
                      <td className="p-2">{s.pack || "—"}</td>
                      <td className="p-2 font-bold text-primary">
                        {s.mode === "received" ? `${s.ticket_start || "000"}–${s.ticket_end || "?"}` : (s.ticket || "—")}
                      </td>
                      <td className="p-2">{s.denom ? `$${s.denom}` : "—"}</td>
                      <td className="p-2">{s.mode === "received" ? (s.packs || 1) : "—"}</td>
                      <td className="p-2 font-bold text-emerald-400" data-testid={`lotto-scan-value-${s.id}`}>
                        {packValue(s) !== null ? `$${packValue(s).toLocaleString()}` : "—"}
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {s.mode === "received" && (
                          <button onClick={() => setEditDialog({ ...s })} className="text-muted-foreground hover:text-primary mr-2" title="Edit delivery" data-testid={`lotto-scan-edit-${s.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => deleteScan(s.id)} className="text-muted-foreground hover:text-rose-400" data-testid={`lotto-scan-delete-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Delivery dialog: denomination + game name required, tickets auto-filled */}
      <Dialog open={!!deliveryDialog} onOpenChange={(o) => { if (!o) { setDeliveryDialog(null); inputRef.current?.focus(); } }}>
        <DialogContent className="max-w-md" data-testid="lotto-delivery-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">New Delivery{deliveryDialog?.game ? ` — Game ${deliveryDialog.game}` : ""}</DialogTitle>
          </DialogHeader>
          {deliveryDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="font-mono text-xs">Game name <span className="text-rose-400">(required)</span></Label>
                <Input
                  value={deliveryDialog.name}
                  onChange={(e) => setDeliveryDialog((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Crossword"
                  className="font-mono"
                  autoFocus
                  data-testid="lotto-delivery-name-input"
                />
              </div>
              {!deliveryDialog.game && (
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs">Game # (optional)</Label>
                  <Input
                    value={deliveryDialog.gameInput || ""}
                    onChange={(e) => setDeliveryDialog((p) => ({ ...p, gameInput: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                    placeholder="758"
                    className="font-mono"
                    data-testid="lotto-delivery-game-input"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="font-mono text-xs">Denomination <span className="text-rose-400">(required)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {DENOMS.map((d) => (
                    <button
                      key={d}
                      onClick={() => setDeliveryDialog((p) => ({ ...p, denom: d }))}
                      className={`px-2.5 py-1 rounded-lg font-mono text-sm font-bold border transition-colors ${deliveryDialog.denom === d ? "bg-emerald-600 text-white border-emerald-700 ring-2 ring-emerald-700/60 shadow-md" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
                      data-testid={`lotto-delivery-denom-${d}`}
                    >
                      ${d}
                    </button>
                  ))}
                </div>
              </div>
              {deliveryDialog.denom && (
                <p className="font-mono text-xs font-medium text-foreground/90" data-testid="lotto-delivery-tickets-preview">
                  Tickets auto-filled from Settings pack size ({packSizes[deliveryDialog.denom] || 30}):{" "}
                  <span className="text-primary font-bold">000–{pad3((packSizes[deliveryDialog.denom] || 30) - 1)}</span>
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeliveryDialog(null); inputRef.current?.focus(); }} data-testid="lotto-delivery-cancel-btn">Cancel</Button>
            <Button
              className="btn-primary"
              disabled={!deliveryDialog?.name?.trim() || !deliveryDialog?.denom}
              onClick={async () => {
                const dd = deliveryDialog;
                const ok = await saveDelivery({
                  raw: dd.raw,
                  game: dd.game || dd.gameInput || "",
                  pack: dd.pack || "",
                  ticket: dd.ticket || "",
                  denom: dd.denom,
                  name: dd.name.trim(),
                });
                if (ok) { setDeliveryDialog(null); inputRef.current?.focus(); }
              }}
              data-testid="lotto-delivery-save-btn"
            >
              Save Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Delivery dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="lotto-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">Edit Delivery{editDialog?.game ? ` — Game ${editDialog.game}` : ""}</DialogTitle>
          </DialogHeader>
          {editDialog && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="font-mono text-xs">Game name</Label>
                <Input
                  value={editDialog.game_name || ""}
                  onChange={(e) => setEditDialog((p) => ({ ...p, game_name: e.target.value }))}
                  className="font-mono"
                  data-testid="lotto-edit-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-mono text-xs">Denomination</Label>
                <div className="flex flex-wrap gap-1.5">
                  {DENOMS.map((d) => (
                    <button
                      key={d}
                      onClick={() => setEditDialog((p) => ({ ...p, denom: d, ticket_start: "000", ticket_end: pad3((packSizes[d] || 30) - 1) }))}
                      className={`px-2.5 py-1 rounded-lg font-mono text-sm font-bold border transition-colors ${editDialog.denom === d ? "bg-emerald-600 text-white border-emerald-700 ring-2 ring-emerald-700/60 shadow-md" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
                      data-testid={`lotto-edit-denom-${d}`}
                    >
                      ${d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs">Packs</Label>
                  <Input
                    type="number"
                    min="1"
                    max="99"
                    value={editDialog.packs || 1}
                    onChange={(e) => setEditDialog((p) => ({ ...p, packs: e.target.value }))}
                    className="font-mono"
                    data-testid="lotto-edit-packs-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs">Ticket start</Label>
                  <Input
                    value={editDialog.ticket_start || ""}
                    onChange={(e) => setEditDialog((p) => ({ ...p, ticket_start: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                    className="font-mono"
                    data-testid="lotto-edit-ticket-start"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs">Ticket end</Label>
                  <Input
                    value={editDialog.ticket_end || ""}
                    onChange={(e) => setEditDialog((p) => ({ ...p, ticket_end: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                    className="font-mono"
                    data-testid="lotto-edit-ticket-end"
                  />
                </div>
              </div>
              <p className="font-mono text-[10px] text-muted-foreground">
                Changing the denomination re-fills tickets from the Settings pack size. Pack count changes also adjust that day's Delivery total.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} data-testid="lotto-edit-cancel-btn">Cancel</Button>
            <Button className="btn-primary" disabled={!editDialog?.game_name?.trim() || !editDialog?.denom} onClick={saveEdit} data-testid="lotto-edit-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
